import React from 'react';
import { Paper, Typography, Button } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { UserRole } from '../../types';

interface AdminAccessControlProps {
  children: React.ReactNode;
  allowedRoles?: UserRole[];
  redirectPath?: string;
  accessDeniedMessage?: string;
}

/**
 * AdminAccessControl Component
 * 
 * Wraps content that should only be accessible to specific user roles.
 * By default, allows SUPER_ADMIN, ADMIN, and EMPLOYEE roles.
 * 
 * @param children - The content to render if access is granted
 * @param allowedRoles - Array of roles that can access this content (default: [SUPER_ADMIN, ADMIN, EMPLOYEE])
 * @param redirectPath - Path to redirect to when access is denied (default: /dashboard)
 * @param accessDeniedMessage - Custom message to show when access is denied
 */
const AdminAccessControl: React.FC<AdminAccessControlProps> = ({
  children,
  allowedRoles = [UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.EMPLOYEE],
  redirectPath = '/dashboard',
  accessDeniedMessage = 'You do not have permission to access this page. Only administrators and staff members can access this section.'
}) => {
  const { user: currentUser } = useAuth();

  // Check if user has permission to access this content
  const hasAccess = currentUser && allowedRoles.includes(currentUser.role);

  if (!hasAccess) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h5" color="error" gutterBottom>
          Access Denied
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {accessDeniedMessage}
        </Typography>
        <Button
          component={RouterLink}
          to={redirectPath}
          variant="contained"
          color="primary"
        >
          Return to Dashboard
        </Button>
      </Paper>
    );
  }

  return <>{children}</>;
};

export default AdminAccessControl;
